import { getAddress } from 'viem';
import type {
  SablierEligibilityResponse,
  SablierEligibilityParams,
  SablierApiError,
} from './sablierTypes';

/**
 * Default Sablier Merkle API base URL
 * Can be overridden via environment variable or function parameter
 */
const DEFAULT_SABLIER_API_URL =
  process.env.REACT_APP_SABLIER_API_URL || 'https://api.sablier.com';

/**
 * Check if an address is eligible for a Sablier airdrop campaign
 *
 * @param params - The eligibility check parameters
 * @returns The eligibility data if the address is eligible, null if not eligible (404 or 400)
 * @throws Error if the API request fails (network error, server error, etc.)
 *
 * @example
 * ```typescript
 * const eligibility = await checkAirdropEligibility({
 *   address: '0x1234...',
 *   cid: 'QmXxx...'
 * });
 *
 * if (eligibility) {
 *   console.log(`Eligible for ${eligibility.amount} tokens`);
 * } else {
 *   console.log('Not eligible');
 * }
 * ```
 */
export async function checkAirdropEligibility(
  params: SablierEligibilityParams,
): Promise<SablierEligibilityResponse | null> {
  const { address, cid, baseUrl = DEFAULT_SABLIER_API_URL } = params;

  // Normalize the address to checksummed format
  let checksummedAddress: string;
  try {
    checksummedAddress = getAddress(address);
  } catch (error) {
    throw new Error(`Invalid Ethereum address: ${address}`);
  }

  // Build the API URL with query parameters
  const url = new URL(`${baseUrl}/api/eligibility`);
  url.searchParams.append('address', checksummedAddress);
  url.searchParams.append('cid', cid);

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // If the address is not eligible, the API returns 404 or 400
    if (response.status === 404 || response.status === 400) {
      return null;
    }

    // Handle other error statuses
    if (!response.ok) {
      const errorData: SablierApiError = await response.json().catch(() => ({
        message: `HTTP error ${response.status}: ${response.statusText}`,
        status: response.status,
      }));

      throw new Error(
        errorData.message || `Failed to check eligibility: ${response.statusText}`,
      );
    }

    // Parse and return the eligibility data
    const data: SablierEligibilityResponse = await response.json();
    return data;
  } catch (error) {
    // Re-throw network errors or parsing errors
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Unknown error occurred while checking airdrop eligibility');
  }
}

/**
 * Check if an address is eligible for multiple campaigns
 *
 * @param address - The wallet address to check
 * @param cids - Array of campaign CIDs to check
 * @param baseUrl - Optional base URL for the Sablier API
 * @returns Array of eligibility results (null for non-eligible campaigns)
 *
 * @example
 * ```typescript
 * const results = await checkMultipleCampaigns(
 *   '0x1234...',
 *   ['QmXxx...', 'QmYyy...']
 * );
 * ```
 */
export async function checkMultipleCampaigns(
  address: string,
  cids: string[],
  baseUrl?: string,
): Promise<(SablierEligibilityResponse | null)[]> {
  const checks = cids.map((cid) =>
    checkAirdropEligibility({ address, cid, baseUrl }),
  );

  return Promise.all(checks);
}
